import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Chip, IconButton, Tooltip } from "@mui/material";
import { ColDef, ICellRendererParams } from "ag-grid-enterprise";

import { CampaignResponse } from "@/pages/mill/schedule/models/CampaignReponse";
import {
	formatDaysSinceLastBillet,
	getDaysSinceLastBillet,
	TimeSinceLastBilletCell,
} from "@/shared/components/TimeSinceLastBillet";

const SampleStatusChip = ({
	code,
}: {
	code: CampaignResponse["SampleStatusCode"];
}) => {
	const isReTest = code === "R";

	return (
		<Chip
			label={isReTest ? "Re-Test" : "Pending"}
			color={isReTest ? "warning" : "error"}
			size="small"
			sx={{
				height: 18,
				fontSize: "0.65rem",
				fontWeight: 600,
				"& .MuiChip-label": { px: 0.75 },
			}}
		/>
	);
};

export const useHeatsTableData = ({
	onViewMechanicalResults,
}: {
	onViewMechanicalResults: (heat: CampaignResponse) => void;
}) => {
	const columnDefs: ColDef<CampaignResponse>[] = [
		{
			field: "HeatName",
			headerName: "Heat",
			minWidth: 90,
			flex: 1,
		},
		{
			field: "GradeName",
			headerName: "Grade",
			minWidth: 90,
			flex: 1,
		},
		{
			field: "SectionName",
			headerName: "Section",
			minWidth: 90,
			flex: 1,
		},
		{
			field: "Start",
			headerName: "Start",
			minWidth: 110,
			flex: 1,
			valueGetter: (params) =>
				dxtToLocalServerTime(params.data?.Start, "MM/dd HH:mm") || "",
		},
		{
			field: "End",
			headerName: "End",
			minWidth: 110,
			flex: 1,
			valueGetter: (params) =>
				dxtToLocalServerTime(params.data?.End, "MM/dd HH:mm") || "-",
		},
		{
			colId: "TimeSinceLastBillet",
			headerName: "Since Last Billet",
			minWidth: 110,
			flex: 1,
			valueGetter: (params) => getDaysSinceLastBillet(params.data),
			valueFormatter: (params) => formatDaysSinceLastBillet(params.value),
			cellRenderer: TimeSinceLastBilletCell,
		},
		{
			field: "RolledColdTons",
			headerName: "Rolled Tons",
			minWidth: 90,
			flex: 1,
			valueFormatter: (params) =>
				params.value ? Number(params.value).toFixed(2) : "",
		},
		{
			field: "SampleStatusCode",
			headerName: "Status",
			minWidth: 100,
			flex: 1,
			cellRenderer: (params: ICellRendererParams<CampaignResponse>) => (
				<SampleStatusChip code={params.value} />
			),
		},
		{
			colId: "mechanicalResults",
			headerName: "",
			width: 50,
			maxWidth: 50,
			flex: 0,
			sortable: false,
			filter: false,
			cellRenderer: (params: ICellRendererParams<CampaignResponse>) => (
				<Tooltip title="View mechanical tests" arrow>
					<IconButton
						size="small"
						onClick={() => params.data && onViewMechanicalResults(params.data)}
					>
						<VisibilityIcon fontSize="small" />
					</IconButton>
				</Tooltip>
			),
		},
	];

	return { columnDefs };
};
